import { Controller, useForm } from "react-hook-form";
import ConfirmationModal from "../../../../../ui/Modal/ConfirmationModal";
import Modal from "../../../../../ui/Modal/Modal";
import { graphql } from "../../../../../__generated__/gql";
import { useMutation } from "urql";
import useUser from "../../../../../providers/UserProvider/useUser";
import { format } from "date-fns";
import Label from "../../../../../ui/Label/Label";
import Select from "../../../../../ui/Select/Select";
import { useMemo } from "react";
import { useDisclosure } from "../../../../../ui/util/useDisclosure";
import Textarea from "../../../../../ui/Textarea/Textarea";
import styles from "./CancelPlanModal.module.scss";

const cancelReasons = [
  { reason: "I'm just pausing for now", question: null },
  { reason: "I don't need any more articles right now", question: null },
  {
    reason: "I can't afford it / too expensive",
    question: "Would you continue with Macaw if it was cheaper?",
  },
  {
    reason: "I'm not using it enough",
    question: "What could we do to make you use Macaw more?",
  },
  {
    reason: "I'm not getting great results",
    question:
      "Would you be interested in a review session to discuss performance?",
  },
  {
    reason: "The article quality is poor",
    question: "What issues are you seeing with quality?",
  },
  {
    reason: "I'm experiencing too many bugs",
    question: "What types of bugs are you experiencing?",
  },
  { reason: "Other", question: "Please explain your reason" },
];

const CancelPlanMutation = graphql(/* GraphQL */ `
  mutation cancelPlan($reason: String!, $comment: String) {
    downgradePlan(billingTier: FREE, reason: $reason, comment: $comment) {
      downgradeBillingTier
      billingTier
    }
  }
`);

type FormData = {
  reason: string;
  comment?: string;
};

function InternalCancelReasonModal({ onClose }: { onClose: () => void }) {
  const [, cancelPlan] = useMutation(CancelPlanMutation);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting },
  } = useForm<FormData>({});

  const [reason, comment] = watch(["reason", "comment"]);

  const onSubmit = async (data: FormData) => {
    await cancelPlan({
      reason: data.reason,
      comment: data.comment,
    });
    onClose();
  };

  const question = useMemo(() => {
    const selectedReason = cancelReasons.find(
      (cancelReason) => cancelReason.reason === reason,
    );

    return selectedReason?.question;
  }, [reason]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
      <ConfirmationModal
        onClose={onClose}
        confirmationText="Submit and downgrade"
        confirmDisabled={!reason || (!!question && !comment)}
        loading={isSubmitting}
      >
        <Label label="Why don't you want to use Macaw any more?">
          <Controller
            control={control}
            name="reason"
            render={({ field }) => (
              <Select
                {...field}
                onChange={(value) => {
                  field.onChange(value);
                  setValue("comment", "");
                }}
                placeholder="Select a reason"
                withinPortal
                data={cancelReasons.map(({ reason }) => ({
                  label: reason,
                  value: reason,
                }))}
              />
            )}
          />
        </Label>
        {question && (
          <Label label={question}>
            <Controller
              control={control}
              name="comment"
              render={({ field }) => (
                <Textarea placeholder="Answer" {...field} />
              )}
            />
          </Label>
        )}
      </ConfirmationModal>
    </form>
  );
}

function CancelReasonModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="We're sad to see you go 😪"
      showHeaderImage
      headerImage="crying"
    >
      <InternalCancelReasonModal onClose={onClose} />
    </Modal>
  );
}

function CancelPlanModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { billingInfo } = useUser();

  const [cancelReasonModalOpened, cancelReasonModal] = useDisclosure();

  const isFirstModalOpen = isOpen && !cancelReasonModalOpened;
  const isCancelReasonModalOpen = isOpen && cancelReasonModalOpened;

  const closeAllModals = () => {
    onClose();
    cancelReasonModal.close();
  };

  if (!billingInfo.currentPeriodEnd) {
    return null;
  }

  return (
    <>
      <Modal
        isOpen={isFirstModalOpen}
        onClose={closeAllModals}
        title="Cancel plan?"
      >
        <p style={{ marginBottom: "8px" }}>
          Your plan will be downgraded to <b>Free</b> at the end of your current
          billing cycle on{" "}
          <b>{format(new Date(billingInfo.currentPeriodEnd), "PP")}</b>
        </p>
        <ConfirmationModal
          onClose={onClose}
          onSubmit={cancelReasonModal.open}
          confirmationText="Continue"
        />
      </Modal>
      <CancelReasonModal
        isOpen={isCancelReasonModalOpen}
        onClose={closeAllModals}
      />
    </>
  );
}

export default CancelPlanModal;
